// © Thibault Sabathier, 2026.
// frontend/src/utils/schoolRoles.js
//
// Constantes front « fonction dans l'établissement » (alumni, multi-valeurs).
// Aligné sur backend/config/schoolRoles.js (MÊMES clés, MÊME ordre).
//
// ⚠️ Convention : la clé DB/API est `school_roles` (snake). Le front manipule le
// tableau de clés tel quel ; le libellé passe par i18n.
//
// Ajouter une clé → propager dans :
//   1. backend/config/schoolRoles.js (VALUES — la colonne étant JSON, aucune
//      migration n'est nécessaire pour ajouter une clé)
//   2. cette liste (SCHOOL_ROLE_VALUES)
//   3. i18n business:school_roles.<key> (fr/en/es/it/pt)

export const SCHOOL_ROLE_VALUES = [
  'enseignant',
  'direction',
  'coordination_pedagogique',
  'relations_entreprises',
  'administratif',
  'referent_stage_alternance',
  'ancien_personnel',
  'bureau_association',
]

// Couleur unique des badges. `primary` n'est pas un choix arbitraire :
//
//  1. C'était déjà la couleur du badge « Enseignant(e) » issu de
//     member_type='professor' (memberTypeConfig.badgeColors.professor). Les
//     enseignants existants garderont donc la même couleur après la bascule :
//     continuité visuelle à travers la migration.
//  2. Elle DIFFÈRE de `info`, couleur des badges intervention_roles. Sans quoi
//     un alumni portant à la fois school_roles=['enseignant'] et
//     intervention_roles=['enseignant'] afficherait deux badges bleus quasi
//     identiques, sans moyen de distinguer « j'enseigne ici » de
//     « j'accepterais de venir enseigner ».
export const SCHOOL_ROLE_BADGE_COLOR = 'primary'

// Libellé affichable d'un rôle. Avec `t` → i18n (business:school_roles.<key>).
export const getSchoolRoleLabel = (key, t) => {
  if (!key) return ''
  if (t) return t(`business:school_roles.${key}`, { defaultValue: key })
  return key
}

/**
 * Ordonne les rôles d'un membre selon la TAXONOMIE, et non selon l'ordre
 * historique du tableau JSON. Écarte au passage les clés inconnues.
 *
 * POURQUOI CETTE FONCTION EXISTE
 *
 * Deux chemins d'écriture produisent des ordres différents en base :
 *   - la route admin passe par `sanitizeSchoolRoles`, qui réordonne ;
 *   - le backfill de la migration 196 fait un JSON_ARRAY_APPEND, donc ajoute
 *     `enseignant` À LA FIN (["direction","enseignant"]).
 *
 * Deux membres aux mêmes rôles peuvent donc avoir un ordre différent en base.
 * Laisser l'ordre du JSON piloter l'affichage ferait varier la position des
 * badges d'une fiche à l'autre, sans raison visible pour le lecteur.
 *
 * On itère donc sur SCHOOL_ROLE_VALUES et non sur l'entrée : c'est ce qui rend
 * le rendu déterministe ET filtre les clés hors taxonomie dans le même geste.
 *
 * @param {unknown} roles - member.school_roles (peut être null/undefined)
 * @returns {string[]} rôles connus, dans l'ordre de la taxonomie
 */
export const sortSchoolRoles = (roles) => {
  if (!Array.isArray(roles) || roles.length === 0) return []
  return SCHOOL_ROLE_VALUES.filter((v) => roles.includes(v))
}

/**
 * Options react-select pour les filtres et l'éditeur admin.
 * @param {function} [t] - i18next
 * @returns {{value: string, label: string}[]}
 */
export const buildSchoolRoleOptions = (t) =>
  SCHOOL_ROLE_VALUES.map((value) => ({ value, label: getSchoolRoleLabel(value, t) }))

/**
 * Prédicat de filtre annuaire / carte : OR sur les rôles sélectionnés.
 *
 * Sélection vide = pas de filtre actif → tout passe. Même invariant que
 * matchesMemberTypeFilter : un filtre non renseigné ne vide JAMAIS l'annuaire.
 *
 * @param {object|null} member
 * @param {string[]} selectedRoles
 * @returns {boolean}
 */
export const matchesSchoolRolesFilter = (member, selectedRoles) => {
  if (!Array.isArray(selectedRoles) || selectedRoles.length === 0) return true
  if (!member) return false
  const roles = Array.isArray(member.school_roles) ? member.school_roles : []
  return roles.some((r) => selectedRoles.includes(r))
}
